// F1 — movimiento compartido (DESIGN_CONTEXT § Dirección visual y movimiento).
// Los valores CSS viven en styles/kw-tokens.css; acá, los mismos para motion.
import { domAnimation, LazyMotion, MotionConfig, type FeatureBundle } from "motion/react";
import { createElement, useEffect, useState, type ReactNode } from "react";

/** Curvas como arrays para motion (las mismas que --kw-ease-*). */
export const KW_EASE = {
  out: [0.23, 1, 0.32, 1],
  inOut: [0.77, 0, 0.175, 1],
  drawer: [0.32, 0.72, 0, 1],
} as const;

/** Duraciones en segundos (las mismas que --kw-duration-*). */
export const KW_DURATION = {
  press: 0.1,
  fast: 0.15,
  base: 0.2,
  slow: 0.28,
} as const;

/** Springs sin rebote (baja frecuencia). Hojas, drawers y la ficha móvil: 0.26 s. */
export const KW_SPRING = {
  sheet: { type: "spring", bounce: 0, duration: 0.26 },
} as const;

/**
 * Alta frecuencia (abrir la hoja de cobro o el panel de peso: en cada venta):
 * ≤ 150 ms ease-out, sin spring. Con teclado (F12, Enter), sin transición.
 */
export const KW_TWEEN_FAST = { type: "tween", duration: KW_DURATION.fast, ease: KW_EASE.out } as const;

/** Frecuencia de un overlay: decide su movimiento (DESIGN_CONTEXT § Movimiento). */
export type KwOverlayFrequency = "low" | "high";

/**
 * Un solo proveedor de motion por app: `LazyMotion` + `domAnimation`, en modo
 * estricto (usar `m.div`, nunca `motion.div`, que trae el bundle completo).
 */
export function KwMotionProvider({
  children,
  features = domAnimation,
}: {
  children: ReactNode;
  /**
   * Por defecto `domAnimation` en el bundle. La tienda pasa un cargador
   * asíncrono para no sumarle el chunk a la entrada: entonces lo visible al
   * cargar no puede depender de una animación de entrada (initial={false}).
   */
  features?: FeatureBundle | (() => Promise<FeatureBundle>);
}) {
  // reducedMotion="user": con prefers-reduced-motion, motion deja solo
  // opacidad y color también en las animaciones que no lo manejan a mano.
  return createElement(
    MotionConfig,
    { reducedMotion: "user" },
    createElement(LazyMotion, { features, strict: true }, children),
  );
}

/**
 * `prefers-reduced-motion` con matchMedia propio (el hook de motion avisa por
 * consola en dev). Reducido = fundidos ≤ 150 ms o nada, sin desplazamientos.
 */
export function useReducedMotionPref(): boolean {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(() =>
    typeof window === "undefined" ? false : window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setReduced(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

export type InteractionSource = "pointer" | "keyboard";

/**
 * De dónde vino una acción. Un botón activado con Enter o Espacio dispara un
 * click con `detail === 0`: cuenta como teclado. Regla: el teclado nunca anima
 * una acción de alta frecuencia (review-animations, F1.0).
 */
export function interactionSource(event: { detail?: number } | null | undefined): InteractionSource {
  return event && typeof event.detail === "number" && event.detail > 0 ? "pointer" : "keyboard";
}
